import { useState, useRef } from "react";
import { FiX, FiImage, FiUpload } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../hooks/useAppStore";
import {
  createStory,
  closeCreator,
  fetchMyStories,
  fetchStoryFeed,
} from "../../store/slices/storiesSlice";
import { selectMyProfile } from "../../store/slices/profileSlice";
import { selectCurrentUser } from "../../store/slices/authSlice";
import { useToast } from "../../hooks/useToast";
import { Button } from "./Button";

export function StoryCreator() {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const myProfile = useAppSelector(selectMyProfile);
  const currentUser = useAppSelector(selectCurrentUser);

  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const url = URL.createObjectURL(f);
    setPreview(url);
  };

  const handlePost = async () => {
    if (!file || !currentUser) return;
    const userId = myProfile?.userId ?? currentUser._id;
    if (!userId) return;

    setUploading(true);
    const fd = new FormData();
    fd.append("media", file);
    if (caption.trim()) fd.append("caption", caption.trim());

    const result = await dispatch(createStory({ userId, formData: fd }));
    setUploading(false);

    if (createStory.rejected.match(result)) {
      const msg = result.payload as string;
      toast(msg || "Failed to post story", "error");
    } else {
      toast("Story posted!", "success");
      dispatch(fetchMyStories());
      dispatch(fetchStoryFeed());
      dispatch(closeCreator());
    }
  };

  const handleClose = () => {
    if (preview) URL.revokeObjectURL(preview);
    dispatch(closeCreator());
  };

  return (
    <div className="fixed inset-0 z-[9998] flex flex-col bg-black/80">
      <div className="flex-none flex items-center justify-between px-5 pt-4 pb-3 bg-charcoal border-b border-white/10">
        <h2 className="font-display font-bold text-lg text-offwhite">
          New Story
        </h2>
        <button
          onClick={handleClose}
          className="p-1 text-offwhite/50 hover:text-offwhite"
        >
          <FiX className="w-5 h-5" />
        </button>
      </div>

      <div className="h-1/2 flex flex-col items-center justify-center px-6 gap-6">
        {preview ? (
          <div className="relative w-full max-w-xs aspect-[9/16] rounded-2xl overflow-hidden bg-black">
            {file?.type.startsWith("video/") ? (
              <video
                src={preview}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-contain"
              />
            ) : (
              <img
                src={preview}
                alt=""
                className="w-full h-full object-contain"
              />
            )}
            <button
              onClick={() => {
                setPreview(null);
                setFile(null);
              }}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center text-white"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-48 h-48 rounded-3xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center gap-3 text-offwhite/40 hover:border-yellow/50 hover:text-yellow/60 transition-colors"
          >
            <FiImage className="w-10 h-10" />
            <span className="text-sm">Choose photo or video</span>
          </button>
        )}

        {preview && (
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            maxLength={150}
            rows={2}
            placeholder="Add a caption… (optional)"
            className="w-full max-w-xs px-4 py-3 rounded-2xl bg-white/5 border border-white/10 text-offwhite text-sm outline-none resize-none placeholder:text-offwhite/25 focus:border-yellow/50"
          />
        )}
      </div>

      <div className="flex-none px-6 pb-6 pt-4 bg-charcoal border-t border-white/10 flex gap-3">
        <Button
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          className="flex-1"
        >
          <FiImage className="w-4 h-4" /> Gallery
        </Button>
        <Button
          onClick={handlePost}
          disabled={!file || uploading}
          className="flex-1"
        >
          {uploading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <FiUpload className="w-4 h-4" /> Post
            </>
          )}
        </Button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}
